/**
 * The theme preference: Light, Dark or System, kept per browser.
 *
 * Light is the default (Mohit, 2 October 2026). A browser that has chosen
 * nothing stores nothing and has no data-theme on <html>, which tokens.css
 * reads as light, so the default needs no script and cannot flash. Dark and
 * System are stored, and set data-theme on <html>. System follows
 * prefers-color-scheme, so a phone that changes at sunset changes the page
 * with it, but only for a student who asked for that.
 *
 * Per browser rather than on the profile, because a student can reasonably want
 * dark on the phone at night and light on the college library's desktop.
 *
 * public/theme-boot.js applies the stored choice before first paint, so an
 * explicit Dark never flashes white. It reads the same key and must stay in
 * step with this file; test/theme.test.ts holds them together.
 */

export type ThemeChoice = "system" | "light" | "dark";

export const THEME_KEY = "ls-theme";

/** The theme a browser has when it has chosen nothing. */
export const DEFAULT_THEME: ThemeChoice = "light";

/** The browser bar colour for each theme, from --paper in tokens.css. */
export const THEME_COLOUR = { light: "#FAFAF9", dark: "#131211" } as const;

export function parseTheme(stored: string | null): ThemeChoice {
  return stored === "light" || stored === "dark" || stored === "system" ? stored : DEFAULT_THEME;
}

export function readTheme(): ThemeChoice {
  try {
    return parseTheme(window.localStorage.getItem(THEME_KEY));
  } catch {
    return DEFAULT_THEME;
  }
}

/** The colour the browser bar should be, for a choice, given what the system says. */
export function barColour(choice: ThemeChoice, systemDark: boolean): string {
  const dark = choice === "dark" || (choice === "system" && systemDark);
  return dark ? THEME_COLOUR.dark : THEME_COLOUR.light;
}

export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === DEFAULT_THEME) root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", choice);

  // One theme-color tag, so the phone's browser bar matches the page and not
  // the phone. System asks the system once; a phone that flips at sunset
  // updates the page at once and the bar the next time the page loads.
  const systemDark = typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches;
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    meta.content = barColour(choice, systemDark);
  }
}

export function saveTheme(choice: ThemeChoice): void {
  try {
    if (choice === DEFAULT_THEME) window.localStorage.removeItem(THEME_KEY);
    else window.localStorage.setItem(THEME_KEY, choice);
  } catch {
    // Nowhere to keep it. The choice still applies for this visit.
  }
  applyTheme(choice);
}
